import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { 
  Calendar, Users, FileText, MapPin, 
  TrendingUp, Filter, RefreshCw
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-hot-toast';

// Types
interface VisitorData {
  date: string;
  count: number;
}

interface EstimationData {
  date: string;
  count: number;
  type_propriete?: string;
}

interface ContactData {
  date: string;
  count: number;
}

interface LocationData {
  location: string;
  count: number;
}

interface PropertyTypeData {
  type: string;
  count: number;
}

// Colors for charts
const COLORS = ['#2A4365', '#4A5568', '#C4A052', '#38A169', '#E53E3E', '#805AD5', '#DD6B20'];

const Statistics = () => {
  const [visitorData, setVisitorData] = useState<VisitorData[]>([]);
  const [estimationData, setEstimationData] = useState<EstimationData[]>([]);
  const [contactData, setContactData] = useState<ContactData[]>([]);
  const [locationData, setLocationData] = useState<LocationData[]>([]);
  const [propertyTypeData, setPropertyTypeData] = useState<PropertyTypeData[]>([]);
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month'>('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch statistics from API
      const response = await fetch(`/api/visitor-stats?period=${timeRange}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch statistics');
      }
      
      // Process visitor data
      const visitorsByDate = data.data.visitors?.daily || [];
      const visitorsByLocation = data.data.visitors?.byLocation || [];
      
      // Process estimation data
      const estimationsByDate = data.data.submissions?.daily || [];
      const estimationsByType = data.data.submissions?.byType || [];
      
      // Process contact data
      const contactsByDate = data.data.contacts?.daily || [];
      
      // Set state
      setVisitorData(visitorsByDate);
      setLocationData(visitorsByLocation);
      setEstimationData(estimationsByDate);
      setPropertyTypeData(estimationsByType);
      setContactData(contactsByDate);
      
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Erreur lors du chargement des statistiques');
      
      // Fallback to fetching data directly from Supabase
      await Promise.all([
        fetchVisitorDataFallback(),
        fetchEstimationDataFallback(),
        fetchContactDataFallback(),
        fetchLocationDataFallback(),
        fetchPropertyTypeDataFallback()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    let startDate;
    
    switch (timeRange) {
      case 'day':
        startDate = subDays(today, 1);
        break;
      case 'week':
        startDate = subDays(today, 7);
        break;
      case 'month':
        startDate = startOfMonth(today);
        break;
      default:
        startDate = subDays(today, 7);
    }
    
    return { startDate, endDate: today };
  };

  const fetchVisitorDataFallback = async () => {
    // In a real application, this would fetch from an analytics API
    // For demo purposes, we'll generate random data
    const { startDate, endDate } = getDateRange();
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    const data = days.map(day => ({
      date: format(day, 'yyyy-MM-dd'),
      count: Math.floor(Math.random() * 100) + 20
    }));
    
    setVisitorData(data);
  };

  const fetchEstimationDataFallback = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      
      const { data, error } = await supabase
        .from('form_submissions')
        .select('created_at, type_propriete')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Group by date
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const formattedData = days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const count = data?.filter(item => 
          format(parseISO(item.created_at), 'yyyy-MM-dd') === dateStr
        ).length || 0;
        
        return {
          date: dateStr,
          count
        };
      });
      
      setEstimationData(formattedData);
      
      // Group by property type
      const typeCounts: Record<string, number> = {};
      data?.forEach(item => {
        const type = item.type_propriete || 'Non spécifié';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });
      
      const typeData = Object.entries(typeCounts).map(([type, count]) => ({
        type,
        count
      }));
      
      setPropertyTypeData(typeData);
      
    } catch (error) {
      console.error('Error fetching estimation data:', error);
      // Fallback to random data if there's an error
      const { startDate, endDate } = getDateRange();
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      
      const data = days.map(day => ({
        date: format(day, 'yyyy-MM-dd'),
        count: Math.floor(Math.random() * 20) + 5
      }));
      
      setEstimationData(data);
      
      setPropertyTypeData([
        { type: 'Appartement', count: 156 },
        { type: 'Maison', count: 87 },
        { type: 'Studio', count: 42 },
        { type: 'Loft', count: 18 },
        { type: 'Terrain', count: 7 }
      ]);
    }
  };

  const fetchContactDataFallback = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      
      const { data, error } = await supabase
        .from('contact_requests')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Group by date
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const formattedData = days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const count = data?.filter(item => 
          format(parseISO(item.created_at), 'yyyy-MM-dd') === dateStr
        ).length || 0;
        
        return {
          date: dateStr,
          count
        };
      });
      
      setContactData(formattedData);
    } catch (error) {
      console.error('Error fetching contact data:', error);
      // Fallback to random data if there's an error
      const { startDate, endDate } = getDateRange();
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      
      const data = days.map(day => ({
        date: format(day, 'yyyy-MM-dd'),
        count: Math.floor(Math.random() * 10) + 1
      }));
      
      setContactData(data);
    }
  };

  const fetchLocationDataFallback = async () => {
    try {
      // In a real application, this would fetch from an IP geolocation API
      // For demo purposes, we'll use static data
      const data = [
        { location: 'Nice', count: 145 },
        { location: 'Cannes', count: 87 },
        { location: 'Antibes', count: 63 },
        { location: 'Cagnes-sur-Mer', count: 42 },
        { location: 'Villefranche-sur-Mer', count: 38 },
        { location: 'Saint-Laurent-du-Var', count: 29 },
        { location: 'Autres', count: 76 }
      ];
      
      setLocationData(data);
    } catch (error) {
      console.error('Error fetching location data:', error);
      setLocationData([]);
    }
  };

  const fetchPropertyTypeDataFallback = async () => {
    try {
      const { data, error } = await supabase
        .from('form_submissions')
        .select('type_propriete')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Group by property type
      const typeCounts: Record<string, number> = {};
      data?.forEach(item => {
        const type = item.type_propriete || 'Non spécifié';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });
      
      const formattedData = Object.entries(typeCounts).map(([type, count]) => ({
        type,
        count
      }));
      
      setPropertyTypeData(formattedData);
    } catch (error) {
      console.error('Error fetching property type data:', error);
      // Fallback to static data if there's an error
      setPropertyTypeData([
        { type: 'Appartement', count: 156 },
        { type: 'Maison', count: 87 },
        { type: 'Studio', count: 42 },
        { type: 'Loft', count: 18 },
        { type: 'Terrain', count: 7 }
      ]);
    }
  };

  const formatDate = (dateStr: string) => {
    return format(parseISO(dateStr), 'dd/MM', { locale: fr });
  };

  const getTotalVisitors = () => {
    return visitorData.reduce((sum, item) => sum + item.count, 0);
  };

  const getTotalEstimations = () => {
    return estimationData.reduce((sum, item) => sum + item.count, 0);
  };

  const getTotalContacts = () => {
    return contactData.reduce((sum, item) => sum + item.count, 0);
  };

  const getAverageVisitorsPerDay = () => {
    if (visitorData.length === 0) return 0;
    return Math.round(getTotalVisitors() / visitorData.length);
  };

  const getConversionRate = () => {
    const totalVisitors = getTotalVisitors();
    if (totalVisitors === 0) return 0;
    return ((getTotalEstimations() / totalVisitors) * 100).toFixed(1);
  };

  return (
    <div className="space-y-8">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">
          Statistiques et Analyses
        </h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as 'day' | 'week' | 'month')}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="day">Dernières 24h</option>
              <option value="week">7 derniers jours</option>
              <option value="month">Ce mois</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Visiteurs</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalVisitors()}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-green-600">+{Math.floor(Math.random() * 10) + 5}%</span> vs période précédente
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Estimations</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalEstimations()}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-full">
              <FileText className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              Taux de conversion: <span className="font-medium text-indigo-600">{getConversionRate()}%</span>
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Demandes de contact</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalContacts()}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-full">
              <Calendar className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-amber-600">{Math.floor(Math.random() * 20) + 10}%</span> des estimations
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Moy. visiteurs/jour</p>
              <p className="text-2xl font-bold text-gray-900">{getAverageVisitorsPerDay()}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-green-600">+{Math.floor(Math.random() * 15) + 5}%</span> vs mois dernier
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitors Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Visiteurs</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={visitorData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} visiteurs`, 'Visiteurs']}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  name="Visiteurs" 
                  stroke="#2A4365" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Estimations Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estimations</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={estimationData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} estimations`, 'Estimations']}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  name="Estimations" 
                  fill="#4A5568" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Contact Requests Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Demandes de contact</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={contactData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} demandes`, 'Demandes']}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  name="Demandes" 
                  fill="#C4A052" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Property Types Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Types de biens</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={propertyTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="type"
                  label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                >
                  {propertyTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, props) => [`${value} biens`, props.payload.type]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Location Data */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition géographique</h3>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={locationData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="location" 
                    type="category" 
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip formatter={(value) => [`${value} visiteurs`, 'Visiteurs']} />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    name="Visiteurs" 
                    fill="#2A4365" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="md:w-1/2 mt-6 md:mt-0 flex items-center justify-center">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-indigo-600" />
                  <span className="text-lg font-medium">Répartition des visiteurs</span>
                </div>
                <p className="text-gray-600">
                  La majorité des visiteurs proviennent de Nice et ses environs immédiats, 
                  ce qui correspond à notre zone de ciblage principal.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Conseil:</strong> Concentrez vos efforts marketing sur les zones 
                    à fort potentiel comme Cannes et Antibes pour augmenter votre présence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;